import { useTelegramContext } from './context'
import type { PopupParams } from '@twa-dev/types'

/**
 * Хук для работы с Telegram Web App SDK
 * 
 * Поддерживает все функции до Bot API 9.1 (July 2025)
 * 
 * @example
 * ```tsx
 * const { webApp, initData, hapticFeedback, CloudStorage, BackButton } = useTelegram()
 * 
 * const handleClick = () => {
 *   hapticFeedback.impactOccurred('medium')
 *   // ... логика
 * }
 * 
 * // Сохранение в облачное хранилище
 * await CloudStorage.setItem('preferences', JSON.stringify({ theme: 'dark' }))
 * ```
 */
export function useTelegram() {
  const { webApp, initData, isVk, vkUserInfo } = useTelegramContext()

  return {
    webApp,
    initData,
    isVk,
    vkUserInfo,
    
    // ==========================================
    // Haptic Feedback
    // ==========================================
    hapticFeedback: {
      impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'medium') => {
        webApp.HapticFeedback?.impactOccurred(style)
      },
      notificationOccurred: (type: 'error' | 'success' | 'warning' = 'success') => {
        webApp.HapticFeedback?.notificationOccurred(type)
      },
      selectionChanged: () => {
        webApp.HapticFeedback?.selectionChanged()
      },
    },
    
    // ==========================================
    // Базовые методы
    // ==========================================
    close: () => {
      webApp.close()
    },
    ready: () => {
      webApp.ready()
    },
    
    // ==========================================
    // Диалоги и попапы
    // ==========================================
    showAlert: (message: string) => {
      webApp.showAlert(message)
    },
    showConfirm: (message: string): Promise<boolean> => {
      return new Promise((resolve) => {
        webApp.showConfirm(message, (confirmed: boolean) => {
          resolve(confirmed)
        })
      })
    },
    showPopup: (params: PopupParams): Promise<string | null> => {
      return new Promise((resolve) => {
        webApp.showPopup(params, (id?: string) => {
          resolve(id ?? null)
        })
      })
    },
    
    // ==========================================
    // Тема и цвета
    // ==========================================
    setHeaderColor: (color: `#${string}` | 'bg_color' | 'secondary_bg_color') => {
      webApp.setHeaderColor(color)
    },
    setBackgroundColor: (color: `#${string}` | 'bg_color' | 'secondary_bg_color') => {
      webApp.setBackgroundColor(color)
    },
    /** Bot API 7.10+ — Цвет нижней панели с кнопками */
    setBottomBarColor: (color: `#${string}` | 'bg_color' | 'secondary_bg_color' | 'bottom_bar_bg_color') => {
      (webApp as any).setBottomBarColor?.(color)
    },
    
    // ==========================================
    // Подтверждение закрытия
    // ==========================================
    enableClosingConfirmation: () => {
      webApp.enableClosingConfirmation()
    },
    disableClosingConfirmation: () => {
      webApp.disableClosingConfirmation()
    },
    
    // ==========================================
    // Вертикальные свайпы (Bot API 7.7+)
    // Используется для предотвращения конфликта с BottomSheet
    // ==========================================
    disableVerticalSwipes: () => {
      (webApp as any).disableVerticalSwipes?.()
    },
    enableVerticalSwipes: () => {
      (webApp as any).enableVerticalSwipes?.()
    },
    isVerticalSwipesEnabled: () => {
      return (webApp as any).isVerticalSwipesEnabled ?? true
    },
    
    // ==========================================
    // MainButton (основная кнопка внизу)
    // ==========================================
    MainButton: {
      text: webApp.MainButton.text,
      color: webApp.MainButton.color,
      textColor: webApp.MainButton.textColor,
      isVisible: webApp.MainButton.isVisible,
      isActive: webApp.MainButton.isActive,
      isProgressVisible: webApp.MainButton.isProgressVisible,
      setText: (text: string) => webApp.MainButton.setText(text),
      onClick: (callback: () => void) => webApp.MainButton.onClick(callback),
      offClick: (callback: () => void) => webApp.MainButton.offClick(callback),
      show: () => webApp.MainButton.show(),
      hide: () => webApp.MainButton.hide(),
      enable: () => webApp.MainButton.enable(),
      disable: () => webApp.MainButton.disable(),
      showProgress: (leaveActive?: boolean) => webApp.MainButton.showProgress(leaveActive),
      hideProgress: () => webApp.MainButton.hideProgress(),
      setParams: (params: {
        text?: string
        color?: string
        text_color?: string
        is_active?: boolean
        is_visible?: boolean
      }) => webApp.MainButton.setParams(params),
    },
    
    // ==========================================
    // SecondaryButton (Bot API 7.10+)
    // Вторая кнопка внизу экрана
    // ==========================================
    SecondaryButton: {
      get text() { return (webApp as any).SecondaryButton?.text ?? '' },
      get color() { return (webApp as any).SecondaryButton?.color ?? '' },
      get textColor() { return (webApp as any).SecondaryButton?.textColor ?? '' },
      get isVisible() { return (webApp as any).SecondaryButton?.isVisible ?? false },
      get isActive() { return (webApp as any).SecondaryButton?.isActive ?? false },
      get isProgressVisible() { return (webApp as any).SecondaryButton?.isProgressVisible ?? false },
      setText: (text: string) => (webApp as any).SecondaryButton?.setText(text),
      onClick: (callback: () => void) => (webApp as any).SecondaryButton?.onClick(callback),
      offClick: (callback: () => void) => (webApp as any).SecondaryButton?.offClick(callback),
      show: () => (webApp as any).SecondaryButton?.show(),
      hide: () => (webApp as any).SecondaryButton?.hide(),
      enable: () => (webApp as any).SecondaryButton?.enable(),
      disable: () => (webApp as any).SecondaryButton?.disable(),
      showProgress: (leaveActive?: boolean) => (webApp as any).SecondaryButton?.showProgress(leaveActive),
      hideProgress: () => (webApp as any).SecondaryButton?.hideProgress(),
      setParams: (params: {
        text?: string
        color?: string
        text_color?: string
        is_active?: boolean
        is_visible?: boolean
        position?: 'left' | 'right' | 'top' | 'bottom'
      }) => (webApp as any).SecondaryButton?.setParams(params),
    },
    
    // ==========================================
    // BackButton (кнопка "Назад" в заголовке)
    // ==========================================
    BackButton: {
      get isVisible() { return webApp.BackButton?.isVisible ?? false },
      show: () => webApp.BackButton?.show(),
      hide: () => webApp.BackButton?.hide(),
      onClick: (callback: () => void) => webApp.BackButton?.onClick(callback),
      offClick: (callback: () => void) => webApp.BackButton?.offClick(callback),
    },
    
    // ==========================================
    // SettingsButton (Bot API 7.0+)
    // Кнопка настроек в заголовке
    // ==========================================
    SettingsButton: {
      get isVisible() { return (webApp as any).SettingsButton?.isVisible ?? false },
      show: () => (webApp as any).SettingsButton?.show(),
      hide: () => (webApp as any).SettingsButton?.hide(),
      onClick: (callback: () => void) => (webApp as any).SettingsButton?.onClick(callback),
      offClick: (callback: () => void) => (webApp as any).SettingsButton?.offClick(callback),
    },
    
    // ==========================================
    // CloudStorage (Bot API 6.9+)
    // Облачное хранилище, синхронизируется между устройствами
    // Лимиты: ключ до 128 байт, значение до 4096 байт, до 1024 ключей
    // ==========================================
    CloudStorage: {
      setItem: (key: string, value: string): Promise<boolean> => 
        new Promise((resolve) => {
          (webApp as any).CloudStorage?.setItem(key, value, (error: any, success: boolean) => {
            resolve(!error && success)
          }) ?? resolve(false)
        }),
      getItem: (key: string): Promise<string | null> =>
        new Promise((resolve) => {
          (webApp as any).CloudStorage?.getItem(key, (error: any, value: string) => {
            resolve(error ? null : value)
          }) ?? resolve(null)
        }),
      getItems: (keys: string[]): Promise<Record<string, string>> =>
        new Promise((resolve) => {
          (webApp as any).CloudStorage?.getItems(keys, (error: any, values: Record<string, string>) => {
            resolve(error ? {} : values)
          }) ?? resolve({})
        }),
      removeItem: (key: string): Promise<boolean> =>
        new Promise((resolve) => {
          (webApp as any).CloudStorage?.removeItem(key, (error: any, success: boolean) => {
            resolve(!error && success)
          }) ?? resolve(false)
        }),
      removeItems: (keys: string[]): Promise<boolean> =>
        new Promise((resolve) => {
          (webApp as any).CloudStorage?.removeItems(keys, (error: any, success: boolean) => {
            resolve(!error && success)
          }) ?? resolve(false)
        }),
      getKeys: (): Promise<string[]> =>
        new Promise((resolve) => {
          (webApp as any).CloudStorage?.getKeys((error: any, keys: string[]) => {
            resolve(error ? [] : keys)
          }) ?? resolve([])
        }),
    },
    
    // ==========================================
    // Полноэкранный режим (Bot API 8.0+)
    // ==========================================
    requestFullscreen: () => (webApp as any).requestFullscreen?.(),
    exitFullscreen: () => (webApp as any).exitFullscreen?.(),
    get isFullscreen() { return (webApp as any).isFullscreen ?? false },
    
    // ==========================================
    // Safe Area Insets (Bot API 8.0+)
    // Отступы для устройств с notch/Dynamic Island
    // ==========================================
    get safeAreaInset() { 
      return (webApp as any).safeAreaInset ?? { top: 0, bottom: 0, left: 0, right: 0 }
    },
    get contentSafeAreaInset() { 
      return (webApp as any).contentSafeAreaInset ?? { top: 0, bottom: 0, left: 0, right: 0 }
    },
    
    // ==========================================
    // Шаринг (Bot API 7.8+, 8.0+)
    // ==========================================
    
    /**
     * Bot API 8.0+ — Поделиться подготовленным сообщением
     * 
     * Открывает диалог выбора чата с красивым превью сообщения
     * (фото + текст + inline кнопка)
     * 
     * @param msgId - ID подготовленного сообщения от savePreparedInlineMessage
     * @param callback - Callback с результатом (true если отправлено)
     */
    shareMessage: (msgId: string, callback?: (sent: boolean) => void): void => {
      if (typeof (webApp as any).shareMessage !== 'function') {
        console.log('[Telegram] shareMessage not supported')
        callback?.(false)
        return
      }
      
      (webApp as any).shareMessage(msgId, (sent: boolean) => {
        console.log('[Telegram] shareMessage result:', sent)
        callback?.(sent)
      })
    },
    
    /** Bot API 7.8+ — Поделиться в Stories */
    shareToStory: (mediaUrl: string, params?: {
      text?: string
      widget_link?: { url: string; name?: string }
    }) => {
      (webApp as any).shareToStory?.(mediaUrl, params)
    },
    
    // ==========================================
    // Homescreen (Bot API 8.0+)
    // ==========================================
    /** Предложить добавить ярлык на домашний экран */
    addToHomeScreen: () => (webApp as any).addToHomeScreen?.(),
    /** Проверить статус ярлыка: 'unsupported' | 'unknown' | 'added' | 'missed' */
    checkHomeScreenStatus: (): Promise<'unsupported' | 'unknown' | 'added' | 'missed'> =>
      new Promise((resolve) => {
        (webApp as any).checkHomeScreenStatus?.((status: string) => {
          resolve(status as any)
        }) ?? resolve('unsupported')
      }),
    
    // ==========================================
    // Дополнительные функции
    // ==========================================
    
    /** Bot API 6.9+ — Запросить контакт пользователя */
    requestContact: (): Promise<{ sent: boolean; contact?: any }> =>
      new Promise((resolve) => {
        (webApp as any).requestContact?.((sent: boolean, contact: any) => {
          resolve({ sent, contact })
        }) ?? resolve({ sent: false })
      }),
    
    /** Bot API 6.4+ — Показать сканер QR-кодов */
    showScanQrPopup: (params?: { text?: string }): Promise<string | null> =>
      new Promise((resolve) => {
        (webApp as any).showScanQrPopup?.(params ?? {}, (text: string) => {
          resolve(text || null)
        }) ?? resolve(null)
      }),
    /** Bot API 6.4+ — Закрыть сканер QR-кодов */
    closeScanQrPopup: () => (webApp as any).closeScanQrPopup?.(),
    
    /** Bot API 6.4+ — Прочитать текст из буфера обмена */
    readTextFromClipboard: (): Promise<string | null> =>
      new Promise((resolve) => {
        (webApp as any).readTextFromClipboard?.((text: string) => {
          resolve(text || null)
        }) ?? resolve(null)
      }),
    
    /** Bot API 8.0+ — Скачать файл */
    downloadFile: (params: { url: string; file_name: string }): Promise<boolean> =>
      new Promise((resolve) => {
        (webApp as any).downloadFile?.(params, (accepted: boolean) => {
          resolve(accepted)
        }) ?? resolve(false)
      }),
    
    /** Bot API 6.7+ — Переключить в inline-режим */
    switchInlineQuery: (query: string, chatTypes?: ('users' | 'bots' | 'groups' | 'channels')[]) => {
      (webApp as any).switchInlineQuery?.(query, chatTypes)
    },
    
    /** Bot API 6.9+ — Запросить разрешение на запись */
    requestWriteAccess: (): Promise<boolean> =>
      new Promise((resolve) => {
        (webApp as any).requestWriteAccess?.((allowed: boolean) => {
          resolve(allowed)
        }) ?? resolve(false)
      }),
    
    // ==========================================
    // Emoji Status (Bot API 8.0+)
    // ==========================================
    /** Установить emoji статус (требует Premium у пользователя) */
    setEmojiStatus: (customEmojiId: string, params?: { duration?: number }): Promise<boolean> =>
      new Promise((resolve) => {
        (webApp as any).setEmojiStatus?.(customEmojiId, params, (success: boolean) => {
          resolve(success)
        }) ?? resolve(false)
      }),
    /** Запросить разрешение на изменение emoji статуса */
    requestEmojiStatusAccess: (): Promise<boolean> =>
      new Promise((resolve) => {
        (webApp as any).requestEmojiStatusAccess?.((allowed: boolean) => {
          resolve(allowed)
        }) ?? resolve(false)
      }),
    
    // ==========================================
    // Информация о платформе
    // ==========================================
    get platform() { return webApp.platform },
    get version() { return webApp.version },
    get colorScheme() { return webApp.colorScheme },
    get themeParams() { return webApp.themeParams },
    get isExpanded() { return webApp.isExpanded },
    get viewportHeight() { return webApp.viewportHeight },
    get viewportStableHeight() { return webApp.viewportStableHeight },
    get isActive() { return (webApp as any).isActive ?? true },
  }
}


